// Frontend/src/components/payments/PaymentBits.tsx
//
// The small pieces the payments page is built from, the way IssueBits.tsx
// serves the issue pages.
//
// Mobile-first throughout: sizes step UP at `sm:`, never down.

import React from "react";
import { AlertTriangle, Inbox, MoreHorizontal } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { InvoiceStatus } from "@/api/payments";
import type { PaymentLabels } from "@/lib/paymentLabels";
import {
  OVERDUE_ICON,
  OVERDUE_TONE,
  STATUS_ICON,
  STATUS_TONE,
  WAITING_TONE,
  cardRailStyle,
  chipStyle,
  iconWellStyle,
  quietChipStyle,
  waitingOn,
} from "@/lib/paymentStyles";


/* ------------------------------------------------------------------ */
export function StatusChip({
  status,
  labels,
  size = "md",
}: {
  status: InvoiceStatus;
  labels: PaymentLabels;
  size?: "sm" | "md";
}) {
  const Icon = STATUS_ICON[status];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full font-semibold ${
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
      }`}
      style={chipStyle(STATUS_TONE[status])}
    >
      <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
      {labels.status[status]}
    </span>
  );
}

/** The red modifier. Only appears when money is genuinely late. */
export function OverdueChip({ days }: { days?: number | null }) {
  const { t } = useLanguage();
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
      style={chipStyle(OVERDUE_TONE)}
    >
      <OVERDUE_ICON className="h-3 w-3" aria-hidden />
      {days && days > 0
        ? t("hoa.payments.overdueDays", { days })
        : t("hoa.payments.overdue")}
    </span>
  );
}

/**
 * Who has the ball, in words.
 *
 * Quiet on purpose: the rail already carries this in colour, and repeating
 * it loudly would leave the card with two things shouting.
 */
export function WaitingLabel({
  status,
  labels,
  awaitingConfirmation,
}: {
  status: InvoiceStatus;
  labels: PaymentLabels;
  /** PAID but the HOA has not confirmed the money landed -- see
   *  isAwaitingHoaConfirmation. Without this the label reads "settled". */
  awaitingConfirmation?: boolean;
}) {
  const who = waitingOn(status, awaitingConfirmation);
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-gray-600">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: WAITING_TONE[who].rail }}
        aria-hidden
      />
      {labels.waiting[who]}
    </span>
  );
}

/** Unit, period, method — facts that must never compete with status. */
export function QuietChip({
  icon: Icon,
  children,
}: {
  icon?: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={quietChipStyle()}
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden />}
      {children}
    </span>
  );
}

/** The card's focal point: the status icon, tinted. Red when overdue. */
export function StatusWell({
  status,
  overdue,
  size = "md",
}: {
  status: InvoiceStatus;
  overdue: boolean;
  size?: "sm" | "md";
}) {
  const Icon = overdue ? OVERDUE_ICON : STATUS_ICON[status];
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-2xl ${
        size === "sm" ? "h-9 w-9" : "h-11 w-11"
      }`}
      style={iconWellStyle(status, overdue)}
      aria-hidden
    >
      <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} />
    </span>
  );
}

/**
 * The card shell: rounded, glass, with the rail as a rounded LEFT BORDER.
 *
 * Same construction as the issue cards and the dashboard's Card. A
 * positioned bar runs square into the rounded corner and reads as an edge
 * stuck on the side; a border follows the radius.
 */
export function PaymentCardShell({
  status,
  overdue,
  settled,
  awaitingConfirmation,
  children,
  className = "",
}: {
  status: InvoiceStatus;
  overdue: boolean;
  settled?: boolean;
  awaitingConfirmation?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-3xl border-l-4 bg-white/80 ring-1 ring-black/5 shadow-sm shadow-black/[0.03] backdrop-blur-xl transition hover:bg-white hover:shadow-md ${
        settled ? "opacity-[0.85] hover:opacity-100" : ""
      } ${className}`}
      style={cardRailStyle(status, overdue, awaitingConfirmation)}
    >
      {children}
    </div>
  );
}

/**
 * Money, with the currency that matters most given prominence.
 *
 * USD is what the invoice is denominated in and what reduces the balance;
 * bolívares is what the resident actually hands over. Both belong, at
 * different weights, and both need tabular figures so a column of them
 * lines up.
 */
export function Money({
  usd,
  ves,
  size = "md",
  align = "right",
}: {
  usd: number | string;
  ves?: number | string | null;
  size?: "sm" | "md" | "lg";
  align?: "left" | "right";
}) {
  const { language } = useLanguage();
  const locale = language === "es" ? "es-VE" : "en-US";
  const amount = Number(usd) || 0;

  const usdClass =
    size === "lg"
      ? "text-lg font-bold"
      : size === "sm"
      ? "text-[13px] font-semibold"
      : "text-[15px] font-semibold";

  return (
    <span className={`block ${align === "right" ? "text-right" : ""}`}>
      <span className={`${usdClass} tabular-nums text-gray-900`}>
        ${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      {ves !== null && ves !== undefined && Number(ves) > 0 && (
        <span className="mt-0.5 block text-[11px] tabular-nums text-gray-500">
          Bs.{" "}
          {Number(ves).toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Summary tiles
 * ------------------------------------------------------------------ */
export function KpiTile({
  label,
  value,
  sub,
  tone,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone: React.CSSProperties;
  icon?: React.ElementType;
  active?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[11px] font-medium">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </div>
      <div className="mt-0.5 text-xl font-bold tabular-nums sm:text-2xl">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] opacity-75">{sub}</div>}
    </>
  );

  const style = { ...tone, ...(active ? { boxShadow: `inset 0 0 0 2px currentColor` } : null) };
  const base =
    "min-w-[8.5rem] shrink-0 snap-start rounded-2xl px-3.5 py-3 text-left transition sm:min-w-0";

  // A tile that filters must LOOK like it does. Both branches shared one
  // className, so an inert tile was pixel-identical to a working one -- and two
  // of the four on the payments page had no onClick at all, which read as
  // buttons that were broken rather than as figures that were never clickable.
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${base} cursor-pointer hover:brightness-[0.97] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]`}
      style={style}
    >
      {body}
    </button>
  ) : (
    <div className={base} style={style}>
      {body}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * States
 * ------------------------------------------------------------------ */
export function PaymentListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2.5" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-3xl border-l-4 border-gray-200 bg-white/80 p-4 ring-1 ring-black/5"
        >
          <div className="flex animate-pulse items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-2xl bg-gray-200" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3.5 w-2/5 rounded bg-gray-200" />
              <div className="h-3 w-3/5 rounded bg-gray-100" />
              <div className="flex gap-2 pt-1">
                <div className="h-4 w-20 rounded-full bg-gray-100" />
                <div className="h-4 w-14 rounded-full bg-gray-100" />
              </div>
            </div>
            <div className="h-5 w-16 shrink-0 rounded bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PaymentEmptyState({
  title,
  hint,
  action,
  tone = "neutral",
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  tone?: "neutral" | "error";
}) {
  const isError = tone === "error";
  const Icon = isError ? AlertTriangle : Inbox;
  return (
    <div
      className={`flex flex-col items-center gap-3 rounded-3xl px-6 py-12 text-center ${
        isError
          ? "bg-red-50 ring-1 ring-red-200"
          : "border border-dashed border-gray-300 bg-white/70 backdrop-blur"
      }`}
    >
      <span
        className={`grid h-11 w-11 place-items-center rounded-full ${
          isError ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div>
        <p className={`text-sm font-semibold ${isError ? "text-red-800" : "text-gray-800"}`}>
          {title}
        </p>
        {hint && (
          <p
            className={`mx-auto mt-1 max-w-xs text-xs ${
              isError ? "text-red-700" : "text-gray-500"
            }`}
          >
            {hint}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * Styled confirm.
 *
 * The mobile card's delete used `window.confirm`, which is an unstyled OS
 * dialog that ignores the app's language settings on some platforms and
 * cannot show what is about to be lost.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  detail,
  confirmLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  detail?: string;
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useLanguage();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={onCancel} aria-hidden />
      <div className="relative w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:w-[calc(100%-3rem)] sm:max-w-sm sm:rounded-3xl">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-600">{message}</p>
            {detail && <p className="mt-1.5 text-[11px] text-gray-500">{detail}</p>}
          </div>
        </div>
        <div className="mt-5 flex gap-2 pb-[max(0rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onCancel}
            className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="h-11 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? t("common.working") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Row actions
 *
 * One primary button and an overflow menu, rather than up to four buttons
 * in a table cell.
 *
 * Before this a row could carry View proofs, View details, Send reminder,
 * Finalize and Delete — three of them solid indigo, emerald and rose — which
 * wrapped onto three lines, gave every row a different height, and put three
 * competing saturated colours in a column that is meant to be scanned.
 * ------------------------------------------------------------------ */
export function RowActionMenu({
  items,
  label,
}: {
  items: {
    key: string;
    label: string;
    onClick: () => void;
    icon?: React.ElementType;
    tone?: "default" | "danger";
    disabled?: boolean;
  }[];
  label: string;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Stops here: a menu open over a sheet must not also close the sheet.
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-[11rem] overflow-hidden rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/10"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition disabled:opacity-40 ${
                  item.tone === "danger"
                    ? "text-red-700 hover:bg-red-50"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Form vocabulary
 *
 * The three payment modals had grown three different ones: labels at
 * `text-sm font-medium` over `rounded-2xl border` inputs here, bare `text-sm`
 * there, and a mix of `py-2` and `h-11` heights throughout. The issues forms
 * settled on small-caps labels over `h-11 rounded-xl ring-1` controls, and
 * these are that, so a field means the same thing in both halves of the app.
 * ------------------------------------------------------------------ */

/** A titled group of fields. Sections make a long form scannable. */
export function FormPanel({
  title,
  hint,
  children,
}: {
  title?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl bg-white/80 p-3.5 ring-1 ring-black/5">
      {title && (
        <header className="mb-3">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
            {title}
          </h3>
          {hint && <p className="mt-0.5 text-[12px] text-gray-500">{hint}</p>}
        </header>
      )}
      <div className="space-y-3.5">{children}</div>
    </section>
  );
}

export function FormField({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {hint && !error && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-[11px] font-medium text-red-600">{error}</p>}
    </div>
  );
}

/** One height, one radius, one focus ring for every control in a form. */
export const fieldClass =
  "h-11 w-full rounded-xl bg-white px-3 text-[15px] text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93] disabled:bg-gray-50 disabled:text-gray-500";

export const areaClass =
  "w-full rounded-xl bg-white px-3 py-2.5 text-[15px] text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]";

/** Cancel + submit, pinned in a modal footer. */
export function FormFooter({
  onCancel,
  submitLabel,
  busyLabel,
  busy,
  disabled,
  form,
  cancelLabel,
}: {
  onCancel: () => void;
  submitLabel: string;
  busyLabel: string;
  busy?: boolean;
  disabled?: boolean;
  /** id of the <form> this submits, so the button can live outside it. */
  form: string;
  cancelLabel: string;
}) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50"
      >
        {cancelLabel}
      </button>
      <button
        type="submit"
        form={form}
        disabled={busy || disabled}
        className="h-11 flex-[2] rounded-xl text-sm font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:opacity-50"
        style={{ background: "linear-gradient(100deg,#1F3A93 0%,#1F3A93 55%,#27AE60 160%)" }}
      >
        {busy ? busyLabel : submitLabel}
      </button>
    </div>
  );
}
